import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as React from "react";
import { type ClassValue, tv } from "tailwind-variants";
import useKeyboardFocus from "../../../hooks/useKeyboardFocus";
import { cn } from "../../../util/classNameConstructor";
import IconContainer from "../../atoms/icons/IconContainer";
import Checkbox from "../checkbox/Checkbox";
import Icon from "../icon/Icon";
import type { IconName } from "../icon/names";

const MenuItemVariant = tv({
	slots: {
		base: "focus-visible:outline-text-default group/menu-item inline-flex h-16 w-full grow items-center gap-4 rounded-lg px-4 py-3 outline-offset-[-2px] focus-visible:outline-2 focus-visible:hover:outline-none",
		textArea: "flex flex-1 flex-col justify-center gap-1",
		primaryText:
			"body-md-semibold text-text-default line-clamp-2 hyphens-auto text-left leading-none -my-1 py-1",
		secondaryText: "body-sm text-text-subdued text-left",
		dividerStyle:
			"mr-3 [.group/menu-item:is(:hover,_.selected)_&]:opacity-0 [.group/menu-item:is(:hover,_.selected)_+_*_&]:opacity-0",
		iconStyle: "",
	},
	variants: {
		action: { true: { base: "pr-3.5" } },
		selected: {
			true: { wrapper: "selected", base: "bg-surface-highlight-subdued" },
		},
		hoverable: {
			true: {
				base: "hover:bg-action-tertiary-hovered active:bg-action-tertiary-pressed cursor-pointer",
			},
		},
		multiLine: {
			false: {
				base: "h-11 gap-2 rounded-lg px-2 py-4",
				textArea: "justify-center",
				dividerStyle: "ml-10",
			},
			true: { base: "box-border [&>*:last-child]:h-9", dividerStyle: "ml-14" },
		},
		iconOption: {
			default: { iconStyle: "p-px" },
			container: { iconStyle: "bg-palette-yellow-400 rounded-lg p-1.5" },
		},
	},
});

export interface MenuItemProps
	extends Omit<React.HTMLAttributes<HTMLElement>, "id"> {
	id?: string;
	index?: number;
	leadingIcon?: IconName;
	leadingIconProps?: React.ComponentProps<typeof Icon>;
	selectable?: boolean;
	selectableType?: "checkbox" | "radio";
	hoverable?: boolean;
	action?: JSX.Element;
	primaryContent: string | JSX.Element;
	secondaryContent?: string;
	selected?: boolean;
	classNames?: Partial<
		Record<keyof (typeof MenuItemVariant)["slots"], ClassValue>
	>;
	lines?: 1 | 2;
	multiSelect?: boolean;
	handleValueChange?: (value: string | null) => void;
	value?: string;
	color?: string;
	number?: number;
	iconOption?: "default" | "container";
}

const MenuItem: React.FC<MenuItemProps> = ({
	id,
	index,
	leadingIcon,
	leadingIconProps,
	selectable = false,
	selectableType,
	hoverable = true,
	primaryContent,
	secondaryContent,
	selected = false,
	action,
	handleValueChange,
	classNames,
	lines,
	value,
	color,
	number,
	iconOption,
	multiSelect,
	...props
}) => {
	const { base, primaryText, secondaryText, textArea } = MenuItemVariant({
		selected,
		hoverable,
		action: !!action,
		multiLine: lines === 2 || !!secondaryContent,
		iconOption,
	});
	const { focusVisible, attachListeners, removeListeners } = useKeyboardFocus();
	const buttonRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		attachListeners(buttonRef.current);
		return () => {
			removeListeners(buttonRef.current);
		};
	}, [attachListeners, removeListeners]);

	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		if (value) {
			handleValueChange?.(value);
		}
	};

	return (
		<DropdownMenu.Item
			className={cn(base(), classNames?.base)}
			ref={buttonRef}
			onClick={handleClick}
			data-index={index}
			{...props}
			onSelect={undefined}
		>
			{selectable && (
				<Checkbox
					checked={selected}
					className="box-content"
					aria-label={`select ${typeof primaryContent === "string" ? primaryContent : "item"}`}
					tabIndex={-1}
					type={selectableType}
				/>
			)}
			{leadingIcon && iconOption === "container" ? (
				<IconContainer
					selectIcon={leadingIcon}
					color={color as any}
					number={number as number}
				/>
			) : leadingIcon ? (
				<Icon name={leadingIcon} {...leadingIconProps} />
			) : null}
			<div className={textArea({ className: classNames?.textArea })}>
				<div className={primaryText({ className: classNames?.primaryText })}>
					{primaryContent}
				</div>
				{secondaryContent && (
					<div
						className={secondaryText({ className: classNames?.secondaryText })}
					>
						{secondaryContent}
					</div>
				)}
			</div>
			{action && action}
		</DropdownMenu.Item>
	);
};

export default MenuItem;
